import { Platform } from 'react-native';
import { I18n } from 'i18n-js';
import { getLocales } from 'expo-localization';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ar } from './ar';
import { en } from './en';
import { es } from './es';
import { fr } from './fr';
import { pt } from './pt';
import { zh } from './zh';

/**
 * Langue de l'INTERFACE — six langues, français par défaut. L'espagnol, le portugais du Brésil,
 * l'arabe et le chinois simplifié sont des traductions automatiques relues par IA (Claude, 17/10/2026),
 * pas par un locuteur natif.
 *
 * Axe INDÉPENDANT de la langue des documents produits (cvs.output_language, lot 1). On peut
 * naviguer en anglais et générer un CV en français : les intitulés de sections des PDF exportés
 * suivent la langue du DOCUMENT, jamais celle-ci.
 *
 * La préférence vit en LOCAL et non en base. Trois raisons : elle doit s'appliquer AVANT
 * l'authentification, l'écran de connexion étant déjà de l'interface ; la lire en base ajouterait
 * une requête au démarrage sur le chemin critique ; et la même personne sur deux appareils peut
 * légitimement vouloir deux langues.
 *
 * La locale est une variable de MODULE, pas un contexte React : les tests la forcent d'une
 * affectation dans jest.setup, sans provider ni wrapper autour d'un seul `render()`.
 */
export type UiLanguage = 'fr' | 'en' | 'es' | 'pt' | 'ar' | 'zh';

export const UI_LANGUAGES: UiLanguage[] = ['fr', 'en', 'es', 'pt', 'ar', 'zh'];

/**
 * Chaque langue écrite DANS cette langue, jamais traduite : on doit retrouver la sienne même quand
 * l'interface est dans une langue qu'on ne lit pas.
 */
export const NOMS_LANGUES: Record<UiLanguage, string> = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
  pt: 'Português (Brasil)',
  ar: 'العربية',
  zh: '中文（简体）',
};

/**
 * Locale des dates et des nombres (`Intl`). `en-GB` : l'ordre jour-mois-année reste celui des offres
 * françaises. Arabe en chiffres latins : les champs de saisie et les textes traduits les emploient.
 */
const LOCALES_FORMATS: Record<UiLanguage, string> = {
  fr: 'fr-FR',
  en: 'en-GB',
  es: 'es-ES',
  pt: 'pt-BR',
  ar: 'ar-u-nu-latn',
  zh: 'zh-CN',
};

function estLangue(valeur: unknown): valeur is UiLanguage {
  return typeof valeur === 'string' && (UI_LANGUAGES as string[]).includes(valeur);
}

const STORAGE_KEY = 'ui_language';

export const i18n = new I18n({ fr, en, es, pt, ar, zh });

/**
 * Pluriel de l'arabe (règle CLDR) : 0, 1, 2, 3 à 10, 11 à 99, puis le reste. La règle par défaut
 * d'i18n-js, celle de l'anglais, ne connaît que 1 et « le reste » : elle écrirait « منذ 3 شهر », faux
 * de 3 à 10. Chaque forme manquante retombe sur `other`.
 */
function categorieArabe(n: number): string {
  const centaine = n % 100;
  if (n === 0) return 'zero';
  if (n === 1) return 'one';
  if (n === 2) return 'two';
  if (centaine >= 3 && centaine <= 10) return 'few';
  if (centaine >= 11 && centaine <= 99) return 'many';
  return 'other';
}
i18n.pluralization.register('ar', (_i18n, count) => [categorieArabe(count), 'other']);

i18n.defaultLocale = 'fr';
i18n.locale = 'fr';
// Une clé absente d'une traduction retombe sur le français plutôt que d'afficher « [missing … ] ».
i18n.enableFallback = true;

/** Traduit une clé. `options` porte les interpolations et le décompte des pluriels. */
/**
 * Android ne connaît pas `writingDirection` : un paragraphe prend le sens de son premier caractère
 * fort, et une phrase arabe qui commence par « CV » ou un chiffre s'aligne à gauche. La marque U+200F
 * (RLM), invisible, donne ce premier caractère. iOS passe par `components/Texte`, le web par CSS.
 *
 * Réservé à l'AFFICHAGE : une chaîne qui sort de l'app ou sert de valeur passe par `tBrut`.
 */
export function t(key: string, options?: Record<string, unknown>): string {
  const valeur = tBrut(key, options);
  return Platform.OS === 'android' && currentLanguage() === 'ar' ? marquerRtl(valeur) : valeur;
}

/** Sans marque : objet d'e-mail, texte partagé, titre enregistré en base. */
export function tBrut(key: string, options?: Record<string, unknown>): string {
  return i18n.t(key, options);
}

const RLM = '\u200F';

// Certaines clés rendent une liste (lignes de plan) : chaque ligne est un paragraphe à part.
function marquerRtl(valeur: unknown): string {
  if (typeof valeur === 'string') return RLM + valeur;
  if (Array.isArray(valeur)) return valeur.map((v) => (typeof v === 'string' ? RLM + v : v)) as unknown as string;
  return valeur as string;
}

export function currentLanguage(): UiLanguage {
  return estLangue(i18n.locale) ? i18n.locale : 'fr';
}

/**
 * Mentions utiles seulement hors français (« documents disponibles en français », « offres en
 * France ») : la clé existe en français, comme partout, mais ne s'y affiche pas.
 */
export function horsFrancais(): boolean {
  return currentLanguage() !== 'fr';
}

export function localeFormats(): string {
  return LOCALES_FORMATS[currentLanguage()];
}

export function setLanguage(language: UiLanguage): void {
  i18n.locale = language;
}

/**
 * Langue de l'appareil, ramenée à ce qu'on sait produire ; toute autre langue donne le français.
 * Approximations assumées : un appareil en portugais du Portugal reçoit le portugais du Brésil, un
 * appareil en chinois traditionnel reçoit le chinois simplifié.
 */
export function deviceLanguage(): UiLanguage {
  const code = (getLocales()[0]?.languageCode ?? '').toLowerCase();
  return estLangue(code) ? code : 'fr';
}

/**
 * Applique la langue au démarrage : la préférence enregistrée si elle existe, sinon celle de
 * l'appareil. La détection ne joue QU'UNE FOIS — dès que la personne a choisi, son choix est
 * enregistré et ne peut plus être écrasé par la locale du téléphone.
 *
 * Un échec de lecture ne bloque rien : on reste en français, l'application démarre.
 */
export const LOAD_TIMEOUT_MS = 1500;

export async function loadLanguage(): Promise<UiLanguage> {
  let stored: string | null = null;
  try {
    // Course contre un délai court : un getItem qui n'aboutit JAMAIS (et non qui échoue) laisserait
    // sinon l'application sur un écran vide, sans erreur et sans recours. Démarrer en français est
    // une gêne ; un écran blanc permanent est un blocage.
    stored = await Promise.race([
      AsyncStorage.getItem(STORAGE_KEY),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), LOAD_TIMEOUT_MS)),
    ]);
  } catch {
    stored = null;
  }

  const language: UiLanguage = estLangue(stored) ? stored : deviceLanguage();
  setLanguage(language);
  return language;
}

/** Enregistre le choix ET l'applique. L'échec d'écriture n'empêche pas le changement immédiat. */
export async function persistLanguage(language: UiLanguage): Promise<void> {
  setLanguage(language);
  try {
    await AsyncStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Préférence non retenue au prochain lancement : gênant, jamais bloquant.
  }
}
